<template>
  <div
    ref="imageContainer"
    :class="containerClasses"
    class="a-image"
  >
    <img
      v-if="fullResResolvedOnCreate"
      :src="fullResImageSrc"
      :alt="altText"
      :title="titleText"
      :class="classes"
      class="full-res loaded"
    >
    <img
      v-else-if="usePlaceholder"
      :src="placeholderSrc"
      :alt="altText"
      :title="titleText"
      :class="classes"
      class="loaded placeholder"
    >
    <template v-else>
      <img
        ref="fullResImage"
        :alt="altText"
        :title="titleText"
        :class="classes"
        class="full-res"
      >
      <img
        ref="lowResImage"
        :src="lowResImageSrc"
        :alt="altText"
        :title="titleText"
        :class="classes"
        class="loaded low-res"
      >
    </template>
  </div>
</template>

<script>
import PlaceHolderImage from './placeholder.svg'

export default {
  name: 'aimage',
  props: {
    img: {
      validator: (val) => typeof val === 'object' || typeof val === 'string' || val === null || val === undefined,
      required: true
    },
    preset: {
      type: [String, Boolean],
      required: false,
      default: false
    },
    title: {
      type: String,
      required: false,
      default: ''
    },
    alt: {
      type: String,
      required: false,
      default: ''
    },
    classes: {
      type: [String, Array],
      required: false,
      default: () => ([])
    },
    responsive: {
      type: Boolean,
      default: false
    },
    contain: {
      type: Boolean,
      default: false
    },
    cover: {
      type: Boolean,
      default: true
    },
    ratio: {
      type: String,
      default: null
    },
    placeholderUrl: {
      type: String,
      default: ''
    },
    hidePlaceholder: {
      type: Boolean,
      default: false
    },
    refresh: {
      type: Boolean,
      default: false
    },
    zoomOnHover: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      refreshRan: false,
      lowResImageSrc: null,
      fullResImageSrc: null,
      usePlaceholder: false,
      observer: null,
      inView: false,
      // True once fullResImageSrc is known synchronously at creation time -
      // either because serverPrefetch() resolved it during SSR, or because
      // hydration found it already cached in the store from that SSR pass
      // (window.__INITIAL_STATE__ carries filestore.filecache over, so the
      // client's loadImage() cache-check below hits immediately). Both
      // cases render the same markup the server produced, so the full-res
      // image shows immediately with a real src instead of going through
      // the blur-up/IntersectionObserver path - which never fires during
      // SSR anyway (no mounted(), no IntersectionObserver server-side).
      // Instances created purely client-side, with no matching SSR pass
      // (e.g. items scrolled into view via client-side pagination), stay
      // false and keep the existing lazy-load behavior unchanged.
      fullResResolvedOnCreate: false
    }
  },
  computed: {
    containerClasses() {
      const classes = []
      if (this.responsive) classes.push('responsive')

      if (this.contain) classes.push('contain')
      else if (this.cover) classes.push('cover')

      if (this.ratio) classes.push(`ratio-${this.ratio}`)

      if (this.zoomOnHover) classes.push('zoom')

      return classes
    },
    altText() {
      if (this.alt) {
        return this.alt
      }
      else if (this.img && this.img.data) {
        return this.img.data.alt || ''
      }
      return ''
    },
    titleText() {
      if (this.title) {
        return this.title
      }
      else if (this.img && this.img.data) {
        return this.img.data.title || ''
      }
      return ''
    },
    placeholderSrc() {
      if (!this.hidePlaceholder) {
        return this.placeholderUrl || PlaceHolderImage
      }
      return ''
    },
    containerRef() {
      if (this.$refs) {
        return this.$refs.imageContainer || null
      }
      return null
    },
    fullResImageRef() {
      if (this.$refs) {
        return this.$refs.fullResImage || null
      }
      return null
    },
    lowResImageRef() {
      if (this.$refs) {
        return this.$refs.lowResImage || null
      }
      return null
    }
  },
  watch: {
    img: {
      handler(val) {
        this.loadImage()
      },
      deep: true
    },
    preset(val) {
      this.loadImage()
    },
    inView(val) {
      if (val && this.fullResImageSrc) {
        this.loadFullResImage()
      }
    },
    fullResImageSrc(val) {
      if (val && this.inView) {
        this.loadFullResImage()
      }
    }
  },
  created() {
    this.loadImage()
  },
  // Only ever invoked during SSR (vue-server-renderer awaits it per
  // component instance before renderToString finishes) - loadImage()'s own
  // resolution is fire-and-forget (relies on getFileURL's callback, which
  // nothing here awaits), so without this the full-res image always has no
  // src in the SSR output regardless of build, since mounted() - the only
  // other place fullResImageSrc's DOM src actually gets applied - never
  // runs server-side either.
  serverPrefetch() {
    return this.resolveFullResForSSR()
  },
  mounted() {
    // Already rendered with a real src (see fullResResolvedOnCreate above) -
    // no need to observe for a swap that already happened.
    if (this.fullResResolvedOnCreate) return
    if (window.IntersectionObserver && this.containerRef) {
      this.observer = new IntersectionObserver(this.observerCallback)
      this.observer.observe(this.containerRef)
    }
    else {
      this.inView = true
    }
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  },
  methods: {
    resolveFullResForSSR() {
      if (this.fullResImageSrc) {
        this.fullResResolvedOnCreate = true
        return Promise.resolve()
      }
      if (!this.img || !this.img.filepath || !this.img.filestorename) {
        return Promise.resolve()
      }
      const preset = this.preset || this.img.preset || '__original'
      const imgSpec = { ...this.img, preset, refresh: !this.refreshRan && this.refresh }
      return this.$store.dispatch('filestore/getFileURL', imgSpec).then(url => {
        if (url) {
          this.fullResImageSrc = url
          this.fullResResolvedOnCreate = true
        }
      })
    },
    loadImage() {
      if (this.img) {
        let preset = this.preset || this.img.preset || '__original'
        let presetLowRes = preset + '__lowres'
        let imgSpec = {
          ...this.img,
          preset,
          refresh: !this.refreshRan && this.refresh,
          callback: (url) => {
            if (url) {
              this.fullResImageSrc = url
            }
            else {
              this.usePlaceholder = true
            }
          }
        }
        let lowResImgSpec = {
          ...this.img,
          preset: presetLowRes,
          refresh: !this.refreshRan && this.refresh,
          callback: (url) => {
            if (url) {
              this.lowResImageSrc = url
            }
          }
        }
        if (this.img.filepath && this.img.filestorename) {
          if (this.refreshRan || !this.refresh) {
            this.fullResImageSrc = this.$store.getters['filestore/fileCachedImageURL'](imgSpec)
            this.lowResImageSrc = this.$store.getters['filestore/fileCachedImageURL'](lowResImgSpec)
            // Cache hit at creation time - the same signal serverPrefetch
            // sets server-side. On hydration this fires when the server
            // already resolved the image, keeping the client's first
            // render identical to what SSR produced.
            if (this.fullResImageSrc) this.fullResResolvedOnCreate = true
          }
          // serverPrefetch() (above) is the awaited resolution path during
          // SSR - dispatching here too would fire a second, redundant
          // request for the same image.
          if (!this.fullResImageSrc && process.env.SERVER_BUILD !== 'ssr') this.$store.dispatch('filestore/getFileURL', imgSpec)
          if (!this.lowResImageSrc) this.$store.dispatch('filestore/getFileURL', lowResImgSpec)
        }
        else {
          this.usePlaceholder = true
        }
      }
      else {
        this.usePlaceholder = true
      }
      this.refreshRan = true
    },
    observerCallback(val) {
      val.some((item) => {
        if (item.isIntersecting) {
          this.inView = true
          if (this.observer) {
            this.observer.disconnect()
            this.observer = null
          }
          return true
        }
      })
    },
    loadFullResImage() {
      if (this.fullResImageSrc && this.fullResImageRef) {
        const fullResImage = new Image()
        const self = this
        fullResImage.onload = function() {
          if (self && self.fullResImageRef && self.lowResImageRef) {
            self.fullResImageRef.src = this.src
            self.fullResImageRef.classList.add('loaded')
            self.lowResImageRef.classList.remove('loaded')
          }
        }
        fullResImage.src = this.fullResImageSrc
      }
    }
  }
}
</script>

<style lang="scss">
  @mixin responsive-ratio($x,$y) {
    $padding: unquote( ( $y / $x ) * 100 + '%' );
    padding-top: $padding;
  }
  .a-image {
    position: relative;
    overflow: hidden;
    z-index: 1;
    display: flex;
    align-items: center;
    width: 100%;
    @include responsive-ratio(16, 9);
    &.cover {
      img {
        object-fit: cover;
        object-position: center;
      }
    }
    &.contain {
      img {
        object-fit: contain;
      }
    }
    img {
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      width: 100%;
      height: 100%;
      &:not(.loaded) {
        pointer-events: none;
      }
      &.low-res {
        transition-timing-function: ease-in;
        transition: 1.5s;
        z-index: 0;
        &:not(.placeholder) {
          filter: blur(3px);
        }
        &.loaded {
          transition: opacity .25s ease-in, transform .3s ease-in;
        }
      }
      &.full-res {
        transition: opacity .3s ease-in, transform .3s ease-in;
      }
      &.placeholder {
        transition: transform .3s ease-in;
      }
      &.loaded {
        opacity: 1;
      }
      &:not([src]) {
        visibility: hidden;
      }
      &.placeholder {
        object-fit: contain;
      }
    }
    &.ratio-16-10 {
      @include responsive-ratio(16, 10);
    }
    &.ratio-16-9 {
      @include responsive-ratio(16, 9);
    }
    &.ratio-3-2 {
      @include responsive-ratio(3, 2);
    }
    &.ratio-4-3 {
      @include responsive-ratio(4, 3);
    }
    &.ratio-3-4 {
      @include responsive-ratio(3, 4);
    }
    &.ratio-square,
    &.ratio-1-1,
    &.ratio-1-1-round{
      @include responsive-ratio(1, 1);
    }
    &.responsive {
      width: 100%;
      height: 100%;
      padding-top: 0;
    }
    &.zoom {
      img {
        transform: scale(1);
      }
      &:hover {
        img {
          transform: scale(1.1);
        }
      }
    }
  }

</style>
